import { create } from 'zustand';
import type { Permission } from '../auth/permissions';
import type { NavigationConfig } from '../config/navigation';
import apiClient from '../api/client';
import type { Role } from '../auth/roles';
import { useLocaleStore } from './localeStore';

interface Capabilities {
  role: Role;
  permissions: Permission[];
  navigation: NavigationConfig;
  /**
   * WP-9 (Part C): the caller's EFFECTIVE C2 actions — the role matrix ± any per-account
   * overrides an OWNER saved. `permissions` is the finer-grained internal matrix; `actions` is
   * the 47-entry catalogue the endpoints are gated on. Optional so an older backend still loads.
   */
  actions?: string[];
  /** C4 pairs this account is allowed to hold, each unlocked by an OWNER acknowledgment. */
  acknowledgedToxicPairs?: string[];
  /** UI language stored on the user row (migration 091). Optional so an older backend still loads. */
  locale?: string;
}

interface CapabilitiesStore {
  loaded: boolean;
  loading: boolean;
  error: string | null;
  data: Capabilities | null;
  permissionSet: Set<string>;
  /** Effective C2 actions, for screens that gate on the catalogue rather than on permissions. */
  actionSet: Set<string>;
  fetch: () => Promise<void>;
  clear: () => void;
}

// Capability data is the BACKEND's answer to "what may this user do?".
// We never derive it locally — we display whatever /me/capabilities returns.
export const useCapabilitiesStore = create<CapabilitiesStore>((set) => ({
  loaded: false,
  loading: false,
  error: null,
  data: null,
  permissionSet: new Set(),
  actionSet: new Set(),

  async fetch() {
    set({ loading: true, error: null });
    try {
      const res = await apiClient.get<Capabilities>('/me/capabilities');
      // The account's own language, which may differ from whatever this browser last cached —
      // reconcile before the shell renders so a user signing in on a colleague's terminal gets
      // their language, not the previous occupant's.
      useLocaleStore.getState().adopt(res.data.locale);
      set({
        loaded: true,
        loading: false,
        data: res.data,
        permissionSet: new Set(res.data.permissions),
        actionSet: new Set(res.data.actions ?? []),
      });
    } catch (e) {
      set({
        loaded: false,
        loading: false,
        error: e instanceof Error ? e.message : 'capabilities fetch failed',
      });
    }
  },

  clear() {
    set({
      loaded: false,
      loading: false,
      error: null,
      data: null,
      permissionSet: new Set(),
      actionSet: new Set(),
    });
  },
}));
